import { Box, TableCell, TableRow } from "@mui/material";
import { useTranslation } from "../../localization/useTranslation.ts";
import { useTemplateActivityPane } from "./TemplateActivityPane.hooks.ts";
import { DotLegalInformationTooltip, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import DialogHeaderWithInformation from "@common/components/dialogHeaderWithInformation/DialogHeaderWithInformation";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import { AnnualWheelActivityTemplateTableModel, TemplateActivityPaneProps } from "./TemplateActivityPane.types.ts";
import ComplianceAreaWrapper from "../../common/components/complianceAreaWrapper/ComplianceAreaWrapper.tsx";
import { sortIds } from "../../common/utilities.ts";
import SubTaskCountChip from "../../common/components/SubTaskCountChip.tsx";
import MappedFrameworkLink from "../../common/components/MappedFrameworkLink.tsx";
import { isNullOrWhitespace } from "../../common/stringOperations.ts";
import MentionInputRender from "../../common/components/mentionInput/MentionInputRender.tsx";
import PaneLayout, { PaneTitle } from "../../main/panes/paneLayout/PaneLayout";
import { definePane } from "../../main/panes/definePane";

function TemplateActivityPane(props: TemplateActivityPaneProps) {
    const { translateString } = useTranslation();
    const {
        isLoading,
        rows,
        isCreateFromObligationTemplates,
        selectedTemplateId,
        selectTemplate,
        filterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useTemplateActivityPane(props);

    function getTableHeaders(): Array<ITableHeader<AnnualWheelActivityTemplateTableModel>> {
        return [
            { property: "imageUrl", text: "", notSortable: true },
            { property: "customId", text: translateString("id"), sortOverride: (a, b, order) => sortIds(a.customId, b.customId, order === "desc") },
            { property: "name", text: translateString("activity") },
            { property: "generalType", text: translateString("type") },
            { property: "mappedFrameworks", text: translateString("mappedFrameworks"), hideHeader: isCreateFromObligationTemplates },
            { property: "subTasks", text: translateString("subTasks") },
            { property: "complianceAreas", text: translateString("complianceAreas") },
            {
                property: "isScheduled",
                text: isCreateFromObligationTemplates ? translateString("obligationTemplatePlannedOnContract") : translateString("plannedInAnnualWheel"),
            },
        ];
    }

    return (
        <PaneLayout
            header={
                <PaneTitle>
                    <DialogHeaderWithInformation
                        header={translateString("createFromTemplate")}
                        information={translateString("createAnnualWheelActivityFromTemplateInfoText")}
                    />
                </PaneTitle>
            }
        >
            <DotLegalTableWithFilterBar
                filterBar={{
                    isLoading: isLoading,
                    filterData: filterOptions,
                    fetchFilterData: true,
                    onMenuClose: onFilterChange,
                    onRemove: onFilterChange,
                    pinnedFilterIds: pinnedFilterIds,
                    onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                }}
                rowsPerPage={100}
                headers={getTableHeaders()}
                defaultOrderBy={"name"}
                isLoading={isLoading}
                data={rows}
                renderRow={(row, rowIndex) => {
                    const isSelected = selectedTemplateId === row.id;
                    return (
                        <TableRow
                            key={rowIndex}
                            onClick={(event) => {
                                // A framework link leaves for the framework, so it must not also start an activity. The
                                // click still has to bubble on: the stage only makes way for a followed link it sees.
                                if (!isLinkClick(event.target)) {
                                    selectTemplate(row);
                                }
                            }}
                            sx={(theme) => ({
                                cursor: "pointer",
                                backgroundColor: isSelected ? theme.palette.primary.light : "initial",
                                outline: isSelected ? `2px solid ${theme.palette.primary.main}` : "none",
                                outlineOffset: isSelected ? "-2px" : "none",
                                borderRadius: "4px",
                            })}
                        >
                            <TableCell>
                                {isNullOrWhitespace(row.imageUrl) ? undefined : (
                                    <Box sx={{ display: "flex" }}>
                                        <Box height={24} width={24} component={"img"} src={row.imageUrl} />
                                    </Box>
                                )}
                            </TableCell>
                            <TableCell>{row.customId}</TableCell>
                            <TableCell>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    {row.name}{" "}
                                    <DotLegalInformationTooltip
                                        text={
                                            isNullOrWhitespace(row.description) ? undefined : (
                                                <MentionInputRender value={row.description ?? ""} linkColor={"white"} />
                                            )
                                        }
                                    />
                                </Box>
                            </TableCell>
                            <TableCell>{row.generalTypeString}</TableCell>
                            {!isCreateFromObligationTemplates && (
                                <TableCell>
                                    {row.mappedFrameworks.map((x, fwIndex) => (
                                        <React.Fragment key={x.id}>
                                            <MappedFrameworkLink model={x} />
                                            {fwIndex < row.mappedFrameworks.length - 1 && ", "}
                                        </React.Fragment>
                                    ))}
                                </TableCell>
                            )}
                            <TableCell>
                                <SubTaskCountChip subTasks={row.subTasks} index={rowIndex} />
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    {row.complianceAreas.map((x, caIndex) => (
                                        <ComplianceAreaWrapper key={rowIndex + "complianceArea" + caIndex} complianceArea={x.name} />
                                    ))}
                                </Box>
                            </TableCell>
                            <TableCell>{row.isPlannedString}</TableCell>
                        </TableRow>
                    );
                }}
            />
        </PaneLayout>
    );
}

function isLinkClick(target: EventTarget) {
    return target instanceof Element && target.closest("a") !== null;
}

export default definePane({ slot: "stage", component: TemplateActivityPane });
